// Configuração
// Proxy na mesma origem: o nginx (prod) / vite (dev) repassa para o n8n injetando o access-token
const API_BASE_URL = '/api/siptek/';

// Cores
export const colors = {
  bgPrimary: '#FFFFFF',
  textPrimary: '#0E1C28', // rgb(14, 28, 40)
  textSecondary: '#0F172A',
  headerBg: '#0F172A',
  borderLight: '#E2E8F0',
  hoverBg: '#F1F5F9',
  success: '#10B981',
  error: '#EF4444',
  buttonBg: '#122230',
  buttonHover: '#05070a',
  buttonText: '#FFFFFF',
};

// Aguarda o WlExtension estar disponível
const waitForWlExtension = (maxAttempts = 50, interval = 100) => {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    const check = () => {
      if (window.WlExtension) {
        resolve();
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(check, interval);
      } else {
        reject(new Error('WlExtension não está disponível após aguardar'));
      }
    };
    check();
  });
};

// Chatlabel - Obtém informações do usuário
// Retorna Promise com { userId, systemKey }
export const getUserInfo = async () => {
  await waitForWlExtension();
  const userInfo = await window.WlExtension.getInfoUser();
  return userInfo;
};

// Chatlabel - Inicializa extensão
// events (plural) conforme documentação
let isInitialized = false;
// Ref para armazenar os callbacks mais recentes (permite atualizar callbacks sem reinicializar)
let currentCallbacks = null;
// Proteção contra eventos duplicados do WlExtension (trava no nível do service)
let lastEventInfo = { type: null, contactId: null, timestamp: 0 };

export const initializeExtension = async (callbacks) => {
  await waitForWlExtension();
  
  // Sempre atualizar os callbacks mais recentes
  currentCallbacks = callbacks;
  
  // Se já foi inicializado, apenas atualizar os callbacks e retornar
  if (isInitialized) {
    console.log('WlExtension já foi inicializado. Atualizando callbacks.');
    return;
  }
  
  try {
    window.WlExtension.initialize({
      events: {
        onOpenAttendance: (attendance) => {
          // Trava: Proteção contra eventos duplicados do WlExtension (aumentado para 150ms)
          const contactId = attendance?.contactId || attendance?.atendimentoId || attendance?.id || null;
          const now = Date.now();
          
          if (
            lastEventInfo.type === 'onOpenAttendance' &&
            lastEventInfo.contactId === contactId &&
            (now - lastEventInfo.timestamp) < 150
          ) {
            console.log(`[TRAVA SERVICE] Evento onOpenAttendance duplicado ignorado (${now - lastEventInfo.timestamp}ms desde último)`);
            return;
          }
          
          lastEventInfo = { type: 'onOpenAttendance', contactId, timestamp: now };
          
          // Evento é emitido quando se clica no atendimento
          // Sempre usar os callbacks mais recentes
          if (currentCallbacks && currentCallbacks.onOpen) {
            currentCallbacks.onOpen(attendance);
          }
        },
        onFocusAttendance: (attendance) => {
          // Trava: Proteção contra eventos duplicados do WlExtension (aumentado para 150ms)
          const contactId = attendance?.contactId || attendance?.atendimentoId || attendance?.id || null;
          const now = Date.now();
          
          if (
            lastEventInfo.type === 'onFocusAttendance' &&
            lastEventInfo.contactId === contactId &&
            (now - lastEventInfo.timestamp) < 150
          ) {
            console.log(`[TRAVA SERVICE] Evento onFocusAttendance duplicado ignorado (${now - lastEventInfo.timestamp}ms desde último)`);
            return;
          }
          
          lastEventInfo = { type: 'onFocusAttendance', contactId, timestamp: now };
          
          // Evento é emitido quando o atendimento recebe foco
          // Sempre usar os callbacks mais recentes
          if (currentCallbacks && currentCallbacks.onFocus) {
            currentCallbacks.onFocus(attendance);
          }
        },
        onCloseAttendance: (attendance) => {
          // Trava: Proteção contra eventos duplicados do WlExtension (aumentado para 150ms)
          const contactId = attendance?.contactId || attendance?.atendimentoId || attendance?.id || null;
          const now = Date.now();
          
          if (
            lastEventInfo.type === 'onCloseAttendance' &&
            lastEventInfo.contactId === contactId &&
            (now - lastEventInfo.timestamp) < 150
          ) {
            console.log(`[TRAVA SERVICE] Evento onCloseAttendance duplicado ignorado (${now - lastEventInfo.timestamp}ms desde último)`);
            return;
          }
          
          lastEventInfo = { type: 'onCloseAttendance', contactId, timestamp: now };
          
          // Evento é emitido quando a tela do atendimento é fechada
          // Sempre usar os callbacks mais recentes
          if (currentCallbacks && currentCallbacks.onClose) {
            currentCallbacks.onClose(attendance);
          }
        }
      }
    });
    isInitialized = true;
  } catch (err) {
    console.error('Erro ao inicializar WlExtension:', err);
    // Se houver erro, permite tentar novamente
    isInitialized = false;
    throw err;
  }
};

// API - Verifica configuração
export const checkConfig = async (userId) => {
  const response = await fetch(`${API_BASE_URL}check-config`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId })
  });
  
  if (!response.ok) {
    throw new Error(`Erro ao verificar configuração: ${response.status}`);
  }
  
  const text = await response.text();
  if (!text) {
    // Se a resposta estiver vazia, retorna configuração padrão
    return { configured: false };
  }
  
  try {
    const data = JSON.parse(text);
    
    // Se retornar { configured: false }, significa que não está configurado
    if (data.configured === false) {
      return { configured: false };
    }
    
    // Se retornar os dados da configuração, mapear os campos
    return { 
      configured: true, 
      ramal: data.ramal || null,
      usuario: data.usuario || null,
      token: data.token || null,
      cliente_id: data.cliente_id || null,
      userId: data.userId || null,
      systemKey: data.systemKey || null
    };
  } catch (e) {
    console.error('Erro ao parsear JSON:', text);
    return { configured: false };
  }
};

// API - Salva configuração
export const saveConfig = async (userId, systemKey, ramal, usuario, token, cliente_id) => {
  const response = await fetch(`${API_BASE_URL}save-config`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ usuario, token, ramal, cliente_id, systemKey, userId })
  });
  
  const text = await response.text();
  let data = {};
  
  if (text) {
    try {
      data = JSON.parse(text);
    } catch (e) {
      console.error('Erro ao parsear JSON:', text);
    }
  }
  
  // Se a resposta não foi OK (status 400, 500, etc) ou se success é false
  if (!response.ok || data.success === false) {
    const error = new Error(data.error || data.message || `Erro ao salvar configuração: ${response.status}`);
    error.status = response.status;
    error.data = data;
    throw error;
  }
  
  return { success: true, ...data };
};

// API - Busca histórico
export const getHistorico = async (userId, numero, signal = null) => {
  const options = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, numero }),
    signal: signal // Passa o signal para o fetch
  };

  let response;
  try {
    response = await fetch(`${API_BASE_URL}busca-ligacoes`, options);
  } catch (err) {
    // Se a requisição foi cancelada, lança erro específico
    if (err.name === 'AbortError' || (signal && signal.aborted)) {
      throw new Error('AbortError');
    }
    throw err;
  }
  
  const text = await response.text();
  if (!text) {
    return { chamadas: [] };
  }
  
  try {
    const data = JSON.parse(text);
    
    // Se retornar erro
    if (data.success === false) {
      // Se for "histórico indisponível", retorna array vazio
      if (data.return === "histórico indisponível") {
        return { chamadas: [] };
      }
      // Outros erros
      throw new Error(data.error || data.return || 'Erro ao buscar histórico');
    }
    
    // Se o retorno for um array direto, envolve em objeto com chamadas
    if (Array.isArray(data)) {
      return { chamadas: data };
    }
    
    // Se não for array e não tiver success: false, pode ser que venha em outro formato
    return { chamadas: [] };
  } catch (e) {
    // Se o erro foi lançado acima, propaga
    if (e.message && e.message.includes('Erro ao buscar histórico')) {
      throw e;
    }
    console.error('Erro ao parsear JSON:', text);
    return { chamadas: [] };
  }
};

// API - Inicia ligação
export const iniciarLigacao = async (userId, numero) => {
  const response = await fetch(`${API_BASE_URL}iniciar-ligacao`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, numero })
  });
  
  const text = await response.text();
  let data = {};
  
  if (text) {
    try {
      data = JSON.parse(text);
    } catch (e) {
      console.error('Erro ao parsear JSON:', text);
    }
  }
  
  // Se a resposta não foi OK ou se success é false
  if (!response.ok || data.success === false) {
    const error = new Error(data.error || data.message || `Erro ao iniciar ligação: ${response.status}`);
    error.status = response.status;
    error.data = data;
    throw error;
  }
  
  // Retorno de sucesso: { http_response_code, mensagem, chamada_id }
  return data;
};

